// estabelecimentoBloqueio
//
// Avalia se uma loja está apta a ativar/destacar conteúdo de marketing
// (cupons de desconto e promoções/campanhas). As regras são compartilhadas
// entre os dois domínios; só as mensagens exibidas mudam (parametrizadas
// por `contexto`).
//
// API:
//   calcularMetricasCancelamento(pedidos)
//   isBloqueadoPorCancelamento(metricas, { isCardapioDigital })
//   avaliarBloqueio({ estabelecimento, metricas, contexto })
//
// `contexto` aceita: 'cupom' | 'promocao' | 'marketing'.

const MENSAGENS = {
  cupom: {
    semEstabelecimento: {
      titulo: 'Selecione uma loja',
      mensagem: 'Selecione uma loja para gerenciar cupons de desconto.',
    },
    suspenso: {
      titulo: 'Loja suspensa — criação de cupons indisponível',
      mensagem: 'Sua loja está suspensa. Não é possível criar ou ativar cupons enquanto o contrato estiver suspenso. Entre em contato com o Suporte para regularizar.',
    },
    cancelamentoTitulo: 'Cupons de destaque bloqueados',
    cancelamentoSuffix: 'Reduza seus cancelamentos para poder ativar cupons de destaque.',
  },
  promocao: {
    semEstabelecimento: {
      titulo: 'Selecione uma loja',
      mensagem: 'Selecione uma loja para gerenciar promoções.',
    },
    suspenso: {
      titulo: 'Loja suspensa — promoções indisponíveis',
      mensagem: 'Sua loja está suspensa. Não é possível ativar promoções enquanto o contrato estiver suspenso. Entre em contato com o Suporte para regularizar.',
    },
    cancelamentoTitulo: 'Ativação de promoções bloqueada',
    cancelamentoSuffix: 'Reduza seus cancelamentos para poder ativar promoções.',
  },
  marketing: {
    semEstabelecimento: {
      titulo: 'Selecione uma loja',
      mensagem: 'Selecione uma loja para acessar as ferramentas de marketing.',
    },
    suspenso: {
      titulo: 'Loja suspensa',
      mensagem: 'Sua loja está suspensa. Não é possível criar cupons nem ativar promoções enquanto o contrato estiver suspenso. Entre em contato com o Suporte para regularizar.',
    },
    cancelamentoTitulo: 'Alta taxa de cancelamento',
    cancelamentoSuffix: 'Reduza seus cancelamentos para liberar cupons de destaque e a ativação de promoções.',
  },
};

// Pedido de teste (realizado no onboarding e cancelado pela equipe).
// Não conta na taxa de cancelamento que bloqueia ações promocionais.
export const TIPO_CANCELAMENTO_TESTE = 3;

export function calcularMetricasCancelamento(pedidos) {
  const lista = (pedidos || []).filter(
    o => Number(o.tipo_cancelamento) !== TIPO_CANCELAMENTO_TESTE
  );
  const totalPedidos = lista.length;
  const qtdCancelados = lista.filter(o => Number(o.status) === 3).length;
  const taxaCancelamento = totalPedidos > 0 ? (qtdCancelados / totalPedidos) * 100 : 0;
  return {
    totalPedidos,
    qtdCancelados,
    taxaCancelamento: Math.round(taxaCancelamento * 10) / 10,
  };
}

export function isBloqueadoPorCancelamento(metricas, opts) {
  const isCardapioDigital = !!(opts && opts.isCardapioDigital);
  if (isCardapioDigital) return false;
  if (!metricas || !metricas.totalPedidos) return false;
  if (metricas.totalPedidos > 10 && metricas.taxaCancelamento > 50) return true;
  if (metricas.totalPedidos <= 10 && metricas.taxaCancelamento > 75) return true;
  return false;
}

function pedidosParaRegularizar(metricas) {
  const total = metricas.totalPedidos;
  const cancelados = metricas.qtdCancelados;
  let aceitos = 0;
  // Simula aceitar pedidos (sem novos cancelamentos) até sair do bloqueio.
  while (aceitos < 1000) {
    aceitos++;
    const novoTotal = total + aceitos;
    const novaTaxa = Math.round((cancelados / novoTotal) * 100 * 10) / 10;
    const novasMetricas = {
      totalPedidos: novoTotal,
      qtdCancelados: cancelados,
      taxaCancelamento: novaTaxa,
    };
    if (!isBloqueadoPorCancelamento(novasMetricas, { isCardapioDigital: false })) {
      return aceitos;
    }
  }
  return aceitos;
}

function montarMensagemCancelamento(metricas, suffix) {
  return 'Sua taxa de cancelamento nos últimos 28 dias está alta. ' + suffix;
}

export function avaliarBloqueio({ estabelecimento, metricas, contexto }) {
  const msgs = MENSAGENS[contexto] || MENSAGENS.cupom;
  const contratoSuspenso = !!(estabelecimento && estabelecimento.status_delivery == 4);
  const isCardapioDigital = !!(estabelecimento && estabelecimento.cardapio_digital == 1);
  const bloqueadoPorCancelamento = isBloqueadoPorCancelamento(metricas, { isCardapioDigital });

  let motivo = null;
  if (!estabelecimento || !estabelecimento.id) {
    motivo = {
      tipo: 'sem_estabelecimento',
      titulo: msgs.semEstabelecimento.titulo,
      mensagem: msgs.semEstabelecimento.mensagem,
    };
  } else if (contratoSuspenso) {
    motivo = {
      tipo: 'suspenso',
      titulo: msgs.suspenso.titulo,
      mensagem: msgs.suspenso.mensagem,
    };
  } else if (bloqueadoPorCancelamento && metricas) {
    motivo = {
      tipo: 'cancelamento',
      titulo: msgs.cancelamentoTitulo,
      mensagem: montarMensagemCancelamento(metricas, msgs.cancelamentoSuffix),
      pedidosParaRegularizar: pedidosParaRegularizar(metricas),
    };
  }

  return { contratoSuspenso, isCardapioDigital, bloqueadoPorCancelamento, motivo };
}

export default {
  TIPO_CANCELAMENTO_TESTE,
  calcularMetricasCancelamento,
  isBloqueadoPorCancelamento,
  avaliarBloqueio,
};
